import React from "react";
import { useTranslation } from "react-i18next";
import { Col, Container, Row } from "reactstrap";

import KpiTile from "../../../../components/admin/KpiTile";
import PageHeader from "../../../../components/admin/PageHeader";
// Components
import ChargesTableCard from "../../../../components/charges/ChargesTableCard";
// Hooks
import useCharges from "../../../../components/charges/hooks/useCharges";
import useManualPaymentInvoices from "../../../../components/manualPayments/hooks/useManualPaymentInvoices";

const Charges: React.FC = () => {
  const { t } = useTranslation("dashboard", { keyPrefix: "adminPages.charges" });

  const {
    charges,
    isLoading: isLoadingCharges,
    filters,
    setFilters,
    ...chargesProps
  } = useCharges({
    filters: true,
    sort: { createdAt: "down" },
    pagination: true,
  });

  // Separate hook to get total charges count (unaffected by table filters)
  const { pagination: totalPagination } = useCharges({
    pagination: { pageSize: 1 },
    sort: { createdAt: "down" },
  });

  // Separate hook to get failed charges count
  const { pagination: failedPagination } = useCharges({
    filters: { pm_status: { $in: ["failed", "declined"] } },
    pagination: { pageSize: 1 },
    sort: { createdAt: "down" },
  });

  // Get pending manual payment invoices count
  const { pagination: manualInvoicesPagination } = useManualPaymentInvoices({
    pagination: { pageSize: 1 },
  });

  const pendingManualCount = manualInvoicesPagination?.total || 0;

  return (
    <React.Fragment>
      <Container className="mt-4" fluid>
        <PageHeader title={t("title")} subtitle={t("subtitle")} />
        <Row className="mb-3" style={{ rowGap: 12 }}>
          <Col xs={12} sm={6} xl={4}>
            <KpiTile
              label={t("totalCharges")}
              value={totalPagination?.total ?? 0}
              detail={t("totalDescription")}
              icon="fas fa-file-invoice"
              isLoading={isLoadingCharges && !totalPagination?.total}
            />
          </Col>
          <Col xs={12} sm={6} xl={4}>
            <KpiTile
              label={t("failedCharges")}
              value={failedPagination?.total ?? 0}
              tone={(failedPagination?.total ?? 0) > 0 ? "crit" : "ok"}
              detail={t("failedDetail")}
              icon="fas fa-exclamation-triangle"
              to="/billing/missing-payments"
            />
          </Col>
          <Col xs={12} sm={6} xl={4}>
            <KpiTile
              label={t("pendingManualInvoices")}
              value={pendingManualCount}
              tone={pendingManualCount > 0 ? "warn" : "ok"}
              detail={t("clickToManage")}
              icon="fas fa-hand-holding-usd"
              to="/billing/manual-payments"
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <ChargesTableCard
              {...chargesProps}
              filters={filters}
              setFilters={setFilters}
              title={t("realizedCharges")}
              filtersToShow={["user"]}
              columnsToShow={["user"]}
              charges={charges}
              isLoading={isLoadingCharges}
            />
          </Col>
        </Row>
      </Container>
    </React.Fragment>
  );
};

export default Charges;
